import { Component, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '../classes/BaseComponent';
import { StorageHelper } from '../helpers/StorageHelper';
import { Global } from '../classes/Global';
import { BaseService } from '../classes/BaseService';
import { NotificationSettingsService } from '../notification-settings.service';
import { AuthenticationService } from '../authentication.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { InfoService } from '../info.service';
import { LoginHelper } from '../helpers/LoginHelper';

@Component({
  selector: 'app-notification-settings',
  templateUrl: './notification-settings.component.html',
  styleUrls: ['./notification-settings.component.scss'],
  providers: [ NotificationSettingsService, AuthenticationService ]
})
export class NotificationSettingsComponent extends BaseComponent implements OnInit {

  emailNotificationsOn: boolean;
  wishlistOn: boolean;
  blogPostsOn: boolean;
  commentsOn: boolean;
  otherDealsOn: boolean;
  cardDealsOn: boolean;
  radarOn: boolean;
  musicOn: boolean;
  moviesOn: boolean;
  seasonsOn: boolean;
  audiobooksOn: boolean;
  ebooksOn: boolean;
  appsOn: boolean;
  macappsOn: boolean;
  success: boolean = false;
  error: boolean = false;

  isLoading: boolean = false;

  @ViewChild("authentication", {static: false}) authentication;

  constructor(
    private notificationSettingsService: NotificationSettingsService,
    private authService: AuthenticationService,
    private route: ActivatedRoute,
    protected infoService: InfoService,
  ) {
    super();
  }

  getSessionTokenFromUrl() {
    //Notifier
    const ngUnsubscribe: Subject<any> = new Subject();

    this.route.queryParams.pipe(takeUntil(ngUnsubscribe.asObservable())) //take values until a notifier emits a value
    .subscribe(params => {
      //Check if there is a sessionToken in the url, if yes,
      //The user probably has been redirected on in the app,
      //Get the user data
      const sessionToken = params['sessionToken'];
      if (sessionToken) {
        this.infoService.getUserDataBySessionToken(sessionToken, Global.getCountry()).subscribe(result => {
          StorageHelper.getInstance().save(StorageHelper.keys.sessionToken, sessionToken);
          StorageHelper.getInstance().save(StorageHelper.keys.username, result['username']);
          StorageHelper.getInstance().save(StorageHelper.keys.email, result['mail']);
          StorageHelper.getInstance().save(StorageHelper.keys.profileImageUrl, result['profileImage']);
          StorageHelper.getInstance().save(StorageHelper.keys.uniqueCustomerId, result['uniqueCustomerId']);
          this.authService.setIsLoggedIn(true);
          LoginHelper.getInstance().loginChanged(true);
          this.loadSettings();
        }, err => {});
      }
      else {
        setTimeout(() => {
          this.authentication.openLogin();  
        });
      }

      //notifies that params have been received and route.params can be unsubscribed
      ngUnsubscribe.next();
      ngUnsubscribe.complete();

    });

  }

  ngOnInit() {
    if(this.authService.isLoggedIn()) {
      this.loadSettings();
    }
    else {
      this.getSessionTokenFromUrl();
    }
  }

  loadSettings() {
    this.isLoading = true;
    this.error = false;
    this.notificationSettingsService.getMailFlags(StorageHelper.getInstance().get(StorageHelper.keys.sessionToken), Global.getCountry()).subscribe(res => {
      if(res && res["mailNotificationSettings"]) {
        let mailSettings = res["mailNotificationSettings"];
        this.emailNotificationsOn = mailSettings["globalEnabled"];
        this.musicOn = mailSettings["music"];
        this.moviesOn = mailSettings["movies"];
        this.seasonsOn = mailSettings["tv"];
        this.audiobooksOn = mailSettings["audiobooks"];
        this.ebooksOn = mailSettings["ebooks"];
        this.appsOn = mailSettings["apps"];
        this.macappsOn = mailSettings["macapps"];
        this.wishlistOn = mailSettings["wishlist"];
        this.blogPostsOn = mailSettings["blogEntry"];
        this.commentsOn = mailSettings["comment"];
        this.cardDealsOn = mailSettings["cardDeals"];
        this.radarOn = mailSettings["radar"];
        this.otherDealsOn = (this.musicOn || this.moviesOn || this.seasonsOn || this.audiobooksOn ||
          this.ebooksOn || this.appsOn || this.macappsOn);
      }
      else if(res && res["message"] == BaseService.COULDNT_LOAD_USER) {
        Global.clearSaveUserInfos();
        this.authentication.openLogin();
      }
      this.isLoading = false;
    }, err => {
      this.error = true;
      this.isLoading = false;
    });
  }

  otherDealsChanged(value) {
    this.otherDealsOn = value;
    this.musicOn = value;
    this.moviesOn = value;
    this.seasonsOn = value;
    this.audiobooksOn = value;
    this.ebooksOn = value;
    this.appsOn = value;
    this.macappsOn = value;
  }

  emailNotificationChanged(value) {
    this.emailNotificationsOn = value;
  }

  mediaChanged(value, media) {
    if(!media) return;
    switch(media) {
      case Global.MUSIC:
        this.musicOn = value;
        break;
      case Global.MOVIES:
        this.moviesOn = value;
        break;
      case Global.SEASONS:
        this.seasonsOn = value;
        break;
      case Global.AUDIOBOOKS:
        this.audiobooksOn = value;
        break;
      case Global.EBOOKS:
        this.ebooksOn = value;
        break;
      case Global.APPS:
        this.appsOn = value;
        break;
      case Global.MACAPPS:
        this.macappsOn = value;
        break;
      default:
        break;
    }

    this.otherDealsOn = (this.musicOn || this.moviesOn || this.seasonsOn || this.audiobooksOn ||
      this.ebooksOn || this.appsOn || this.macappsOn);

  }

  wishlistChanged(value) {
    this.wishlistOn = value;
  }

  blogPostsChanged(value) {
    this.blogPostsOn = value;
  }

  commentsChanged(value) {
    this.commentsOn = value;
  }

  cardDealsChanged(value) {
    this.cardDealsOn = value;
  }

  save() {
    let dealFlags: string = "";
    this.isLoading = true;
    if(this.musicOn) { dealFlags += "1"; } else { dealFlags += "0"; }
    if(this.moviesOn) { dealFlags += "1"; } else { dealFlags += "0"; }
    if(this.seasonsOn) { dealFlags += "1"; } else { dealFlags += "0"; }
    if(this.audiobooksOn) { dealFlags += "1"; } else { dealFlags += "0"; }
    if(this.ebooksOn) { dealFlags += "1"; } else { dealFlags += "0"; }
    if(this.appsOn) { dealFlags += "1"; } else { dealFlags += "0"; }
    if(this.macappsOn) { dealFlags += "1"; } else { dealFlags += "0"; }

    //For new API
    let flags: string = "";
    if(this.emailNotificationsOn) { flags += "1"; } else { flags += "0"; }//globalEnabled
    if(this.wishlistOn) { flags += "1"; } else { flags += "0"; }//wishlist
    if(this.radarOn) { flags += "1"; } else { flags += "0"; }//radar
    if(this.blogPostsOn) { flags += "1"; } else { flags += "0"; }//blogEntry
    if(this.commentsOn) { flags += "1"; } else { flags += "0"; }//comment
    if(this.cardDealsOn) { flags += "1"; } else { flags += "0"; }//cardDeals
    flags += dealFlags; //medias

    this.notificationSettingsService.setMailFlags(flags, StorageHelper.getInstance().get(StorageHelper.keys.sessionToken), Global.getCountry()).subscribe(res => {
      if(res && res["message"] == BaseService.COULDNT_LOAD_USER) {
        StorageHelper.getInstance().clear(StorageHelper.keys.sessionToken);
        this.authentication.openLogin();
      }
      else {
        this.error = false;
        this.success = true;
      }
      this.isLoading = false;
    }, err => {
      this.success = false;
      this.error = false;
      this.isLoading = false;
    });

  }

}
